import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { ListViewHeader } from './listViewHeader';
import { Link } from 'react-router-dom';
import { ListViewFooter } from './listViewFooter';
import { ThreeDotsButton } from '../reusableItems/buttons';
import { CheckBoxInput } from '../reusableItems/checkboxInput';
import { userHasAccess } from '../../../supplementary_components/userDataDelegate';
import styles from './listView.module.scss';
import { addAndRemoveLoadingIcon } from '../item_page/driver_page_components/payoutAdjustmentsSection';

// Kept as styled-components for dynamic conditional_formatting (CSS string injection)
const RowItem = styled.div`text-align: ${props => props.alignment || 'left'}; ${props => props.conditional_formatting || ''}`;
const ClickableItem = styled(Link)`text-underline-offset: 0.2rem;  color: var(--dark-gray); text-align: ${props => props.alignment || 'left'}; ${props => props.conditional_formatting || ''}`;

// Exported for teamsContentWrapper - uses styled-component for $customcss injection
// Use $columnProportions to avoid forwarding to DOM
export const Container = styled.div`
    width: 100%;
    display: grid;
    gap: 0;
    grid-template-columns: ${props => props.$columnProportions || props.columnProportions || '1fr'};
    ${props => props.$customcss || ''}
`;

export const ListView = ({listRef, ListConf, Data, Error, NoFooter, RequestReload, PaginationData, filteredData, listBeingUpdated, selectedCategory, noBoxRadius, setData, setFilteredData})=> {
    const [sortingData, setSortingData] = useState(null);
    const [expandedRowId, setExpandedRowId] = useState(null);

    const canAccessItemPage = ListConf && ListConf.minAccessToItemPage ?  userHasAccess(ListConf.minAccessToItemPage) : true;
    const userHassAccessToEdit = ListConf ? userHasAccess(ListConf.editAccessLevel) : false;
    const dataToDisplay = (filteredData && filteredData.dataset) || Data;

    const clickableRowConf = ListConf?.clickableRow;
    const expandingComponent =
        (clickableRowConf && typeof clickableRowConf === "object" && clickableRowConf.expandingComponent) ||
        null;
    // const isRowExpandable = Boolean(expandingComponent);

    const getRowId = useMemo(() => {
        const idKey =
            (typeof clickableRowConf === "object" && clickableRowConf && clickableRowConf.identifierColumnName) ||
            ListConf?.rowIdentifierColumnName ||
            "id";
        return (entity, fallbackIndex) => {
            const val = entity?.[idKey];
            return val !== undefined && val !== null && val !== "" ? val : fallbackIndex;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clickableRowConf, ListConf]);

    const shouldIgnoreRowToggleClick = (e) => {
        try {
            const target = e?.target;
            if (!target || !target.closest) return false;
            // Don't toggle when interacting with inputs/links/buttons inside the row.
            return Boolean(
                target.closest(
                    'a,button,input,textarea,select,label,[role="button"],[data-row-toggle="ignore"]'
                )
            );
        } catch (err) {
            return false;
        }
    };

    const renderRowDetails = useMemo(() => {
        if (!expandingComponent) return null;
        const Component = expandingComponent;
        return (DataEntity, onClose) => <Component DataEntity={DataEntity} onClose={onClose} />;
    }, [expandingComponent]);

    if (Error !== null)return <div className='errorMessage'>Something went wrong</div>
    if ((filteredData && filteredData.dataset.length === 0) || (Array.isArray(Data) && Data.length === 0))return <><div className={`errorMessage ${listBeingUpdated ? styles.listUpdating : ''}`}>
        No {ListConf.dataTitlePlural.toLowerCase()} were found
    </div>
    {filteredData && filteredData.nextBatchLink ?<><br /><br /><LoadMoreButton data={filteredData} customLabel={"Search In Database "} /></>:null}
    </>

    if(sortingData){
        dataToDisplay.sort((a, b) => {
            const valA = sortingData.valueExtractor(a);
            const valB = sortingData.valueExtractor(b);
            // Handle null/undefined values
            if (valA == null && valB == null) return 0;
            if (valA == null) return 1; // nulls go to end
            if (valB == null) return -1;
            // If valuesAreDates flag is set, parse and compare as dates
            if (sortingData.valuesAreDates) {
                const dateA = parseUSDate(valA);
                const dateB = parseUSDate(valB);
                
                if (dateA && dateB) {
                    // Both are valid dates - compare chronologically
                    const timeDiff = dateA.getTime() - dateB.getTime();
                    return sortingData.desc ? -timeDiff : timeDiff;
                }
                // If parsing fails, fall through to string comparison
            }
            // Check if both values are numbers
            const isNumeric = typeof valA === 'number' && typeof valB === 'number' && 
                             !isNaN(valA) && !isNaN(valB);
            
            if (isNumeric) {
                // Numeric comparison
                return sortingData.desc ? valB - valA : valA - valB;
            } else {
                // String comparison
                const strA = String(valA);
                const strB = String(valB);
                const comparison = strA.localeCompare(strB, undefined, { 
                    numeric: true, // Handles "10" > "2" correctly
                    sensitivity: 'base' // Case-insensitive
                });
                return sortingData.desc ? -comparison : comparison;
            }
        })
    }
    if(!Array.isArray(dataToDisplay))return null;
    const summaryConf = ListConf.filteredResultsSummary;
    const filteredSummary = filteredData && summaryConf
        ? `${summaryConf.label}: ${summaryConf.formatter(
              filteredData.dataset.reduce((sum, row) => sum + summaryConf.valueExtractor(row), 0)
          )}${filteredData.nextBatchLink ? "+" : ""}`
        : null;
    return (
        <>
        <div
            ref={listRef}
            className={`${styles.container} ${listBeingUpdated ? styles.listUpdating : ''} ${noBoxRadius ? styles.noBoxRadius : ''}`}
            // eslint-disable-next-line
            style={{ ['--list-columns']: ListConf.columnProportions }}
        >
            {/* // HEADER */}
            <ListViewHeader LabelConf={ListConf} sortingData={sortingData} setSortingData={setSortingData} currentCategory={selectedCategory} columnProportions={ListConf.columnProportions} />
            
            {/* CONTENT */}
            {(dataToDisplay).map((DataEntity, index)=>{
                const rowId = getRowId(DataEntity, index);
                const isExpanded = Boolean(renderRowDetails) && expandedRowId === rowId;
                return (
                    <React.Fragment key={rowId}>
                        <div
                            className={`${styles.row} ${isExpanded ? styles.expanded : ''} ${ListConf.hasSidePadding ? styles.hasSidePadding : ''}`}
                            onClick={
                                renderRowDetails
                                    ? (e) => {
                                        if (shouldIgnoreRowToggleClick(e)) return;
                                        setExpandedRowId((prev) => (prev === rowId ? null : rowId));
                                    }
                                    : undefined
                            }
                            style={renderRowDetails ? { cursor: "pointer" } : undefined}
                        >
                            {ListConf.columns.map((columnData, colIndex) => {
                                if (columnData.type === "select") {
                                    return <div key={colIndex} data-row-toggle="ignore">
                                        
                                        <CheckBoxInput  checked={DataEntity["unselected"]?false:true} onChangeAction={{method: columnData.onChange, parameter:DataEntity[columnData.trackingParameter] }} className={columnData.className}  />
                                    </div>
                                } else if (columnData.customComponent) {
                                    return (
                                        <React.Fragment key={colIndex}>
                                            {columnData.customComponent(
                                                DataEntity,
                                                selectedCategory,
                                                RequestReload,
                                                { setData, setFilteredData },
                                            )}
                                        </React.Fragment>
                                    );
                                }

                                return <React.Fragment key={colIndex}>{ValueExtractor(DataEntity, columnData, RequestReload, canAccessItemPage, userHassAccessToEdit, setData, setFilteredData)}</React.Fragment>
                                    
                            })}
                        </div>
                        {isExpanded ? (
                            <div className={styles.expandedContent}>
                                <div style={{ gridColumn: "1 / -1" }}>
                                    {renderRowDetails(DataEntity, () => setExpandedRowId(null))}
                                </div>
                            </div>
                        ) : null}
                    </React.Fragment>
                );
            })}
        </div>
        {filteredData ?
            <>{filteredData.nextBatchLink ?
                <LoadMoreButton data={filteredData} />
            :null}
            <ListViewFooter resultCount={filteredData.dataset.length} summary={filteredSummary} requestReload={()=>{}} />
            </>
            :null}
        {
            NoFooter || filteredData ?
            <></>
            :
            <ListViewFooter paginationData={PaginationData} resultCount={Data.length} requestReload={RequestReload} />
        }
        
    </>
)}
const LoadMoreButton = ({data, customLabel}) => {
    return <div className={`${styles.loadMoreButtonWrapper} non-selectable` }>
        <div onClick={(e)=>{
            if(!e.target.className.includes(`${styles.loading}`)){
                const target = e.target.childNodes[0];
                e.target.className = styles.loading;
                addAndRemoveLoadingIcon(target, false);
                data.loadMore(()=>{
                    e.target.className = "";
                    addAndRemoveLoadingIcon(target, true);
                }, data.nextBatchLink, data.nextBatchSize);
            }
            
        }}>{customLabel || "Load More "}</div>
    </div>
}
function ValueExtractor (entity, columnData, RequestReload, canAccessItemPage, userHassAccessToEdit, setData, setFilteredData){
    if (columnData.notInListView)return null;
    if (columnData.actionButtons ){
        return <ThreeDotsButton key={1} syncInsteadRemove={columnData.syncInsteadRemove} disabled={(!userHassAccessToEdit  || (columnData.notEditableIfValueIs && columnData.notEditableIfValueIs(entity)))}  position="relative" top="0" left="0" onRemove={()=>{columnData.onRemoveAction(entity, RequestReload)}} onEdit={()=>{columnData.onEditAction(entity, RequestReload, setData, setFilteredData)}} onSync={() => columnData.onSyncAction?.(entity, RequestReload, setData, setFilteredData)} />
    }
    let Wrapper = RowItem;
    let path = null;
    if (columnData.clickable && canAccessItemPage){
        Wrapper = ClickableItem;
        path = columnData.path + entity[columnData.identifierColumnName];

    }
    const value = columnData.valueGenerator ? columnData.valueGenerator(entity) : entity[columnData.columnName];
    return <Wrapper
        key={columnData.label}
        conditional_formatting={columnData.conditionalFormatting != null ? columnData.conditionalFormatting(value) : null}
        alignment={columnData.alignment}
        to={path}
    >
        <span>{columnData.valueInterpreter != null ? columnData.valueInterpreter(value) : value}</span>
    </Wrapper>
}
export function NestedValueExtractor(entity, columnName, dataIsInJSon){
    if(entity === null)return '-';
    if (!Array.isArray(columnName)){
        return entity[columnName] === null || entity[columnName] === undefined || entity[columnName] === ''? '-' :entity[columnName];
    } 
    let result = '';
    
    for (const paths of columnName){
        if (!Array.isArray(paths)){
            result+=paths;
            continue;
        }

        let currentState = entity[paths[0]];
        let savedResult = result
        paths.forEach(function (value, i) {
            if (i) {
                try {
                    if(dataIsInJSon){
                        currentState = JSON.parse(currentState)[value];
                    }else{
                        currentState = currentState[value];
                    }
                    if (i === paths.length-1){
                        if (currentState){
                            savedResult += currentState;
                        }else savedResult += '-';
                        
                    }
                }catch{
                    savedResult += '-';
                }
            }
        })
        result = savedResult;
    }
    // if (result === '- / -' || result === '- -' || result ===  '- (-)')result = '-';
    if (result.search("^[- /,( )]*$") === 0)result = '-';
    return result;
    
}
export function parseUSDate(dateStr) {
    if (typeof dateStr !== 'string') return null;
    // Match MM/DD/YY or MM/DD/YYYY
    const match = dateStr.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/);
    if (!match) return null;
    
    const [, month, day, year] = match;
    const mm = parseInt(month, 10);
    const dd = parseInt(day, 10);
    let yy = parseInt(year, 10);
    
    // Convert 2-digit year to 4-digit (assuming 2000-2099)
    if (yy < 100) {
        yy = 2000 + yy;
    }
    
    // Validate date
    if (mm < 1 || mm > 12 || dd < 1 || dd > 31) return null;
    
    const date = new Date(yy, mm - 1, dd);
    // Verify the date is valid (catches invalid dates like 02/30/25)
    if (date.getFullYear() !== yy || date.getMonth() !== mm - 1 || date.getDate() !== dd) {
        return null;
    }
    
    return date;
}